/**
 * Generated by orval v7.21.0 🍺
 * Do not edit manually.
 * Rocket.net API
 * Rocket.net - All APIs
 * OpenAPI spec version: 1.0.0
 */
import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
  UseSuspenseQueryOptions,
  UseSuspenseQueryResult,
} from '@tanstack/react-query';

import { customFetch } from '../../custom-fetch';
import { addMetaToMutation } from '../../mutators/add-meta';
import { addMetaToQuery } from '../../mutators/add-meta';
import type {
  AcceptAccountUserInviteParams,
  AcceptInviteResponse,
  CreateUserRequest,
  Error,
  ListAccountUsersParams,
  ListUsersResponse,
  SetUserPasswordRequest,
  SuccessResponse,
  UpdateUserRequest,
  UserResponse,
} from '../rocketNetAPI.schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

/**
 * Adds a user to your Rocket.net account. They receive an email to set their password and accept the invite.
 * @summary Invite an Account User
 */
export const createAccountUser = (createUserRequest: CreateUserRequest, signal?: AbortSignal) => {
  return customFetch<UserResponse>({
    url: `/v1/users`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: createUserRequest,
    signal,
  });
};

export const getCreateAccountUserMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createAccountUser>>,
    TError,
    { data: CreateUserRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createAccountUser>>,
  TError,
  { data: CreateUserRequest },
  TContext
> => {
  const mutationKey = ['createAccountUser'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createAccountUser>>,
    { data: CreateUserRequest }
  > = props => {
    const { data } = props ?? {};

    return createAccountUser(data);
  };

  const customOptions = addMetaToMutation(
    { ...mutationOptions, mutationFn },
    { url: `/v1/users` },
    {
      operationId: 'app.controllers.account_users_controller.create_account_user',
      operationName: 'createAccountUser',
    }
  );

  return customOptions;
};

export type CreateAccountUserMutationResult = NonNullable<
  Awaited<ReturnType<typeof createAccountUser>>
>;
export type CreateAccountUserMutationBody = CreateUserRequest;
export type CreateAccountUserMutationError = Error;

/**
 * @summary Invite an Account User
 */
export const useCreateAccountUser = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createAccountUser>>,
      TError,
      { data: CreateUserRequest },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof createAccountUser>>,
  TError,
  { data: CreateUserRequest },
  TContext
> => {
  const mutationOptions = getCreateAccountUserMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Lists all users on your account with roles and status. Search, sort, and paginate results.
 * @summary List Account Users
 */
export const listAccountUsers = (params?: ListAccountUsersParams, signal?: AbortSignal) => {
  return customFetch<ListUsersResponse>({ url: `/v1/users`, method: 'GET', params, signal });
};

export const getListAccountUsersQueryKey = (params?: ListAccountUsersParams) => {
  return [`/v1/users`, ...(params ? [params] : [])] as const;
};

export const getListAccountUsersQueryOptions = <
  TData = Awaited<ReturnType<typeof listAccountUsers>>,
  TError = Error,
>(
  params?: ListAccountUsersParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listAccountUsers>>, TError, TData>>;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListAccountUsersQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listAccountUsers>>> = ({ signal }) =>
    listAccountUsers(params, signal);

  const customOptions = addMetaToQuery(
    { ...queryOptions, queryKey, queryFn },
    { params },
    {
      url: `/v1/users`,
      operationId: 'app.controllers.account_users_controller.list_account_users',
      operationName: 'listAccountUsers',
    }
  );

  return customOptions as UseQueryOptions<
    Awaited<ReturnType<typeof listAccountUsers>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListAccountUsersQueryResult = NonNullable<Awaited<ReturnType<typeof listAccountUsers>>>;
export type ListAccountUsersQueryError = Error;

export function useListAccountUsers<
  TData = Awaited<ReturnType<typeof listAccountUsers>>,
  TError = Error,
>(
  params: undefined | ListAccountUsersParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listAccountUsers>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listAccountUsers>>,
          TError,
          Awaited<ReturnType<typeof listAccountUsers>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListAccountUsers<
  TData = Awaited<ReturnType<typeof listAccountUsers>>,
  TError = Error,
>(
  params?: ListAccountUsersParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listAccountUsers>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listAccountUsers>>,
          TError,
          Awaited<ReturnType<typeof listAccountUsers>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListAccountUsers<
  TData = Awaited<ReturnType<typeof listAccountUsers>>,
  TError = Error,
>(
  params?: ListAccountUsersParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listAccountUsers>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Account Users
 */

export function useListAccountUsers<
  TData = Awaited<ReturnType<typeof listAccountUsers>>,
  TError = Error,
>(
  params?: ListAccountUsersParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listAccountUsers>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListAccountUsersQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

export const getListAccountUsersSuspenseQueryOptions = <
  TData = Awaited<ReturnType<typeof listAccountUsers>>,
  TError = Error,
>(
  params?: ListAccountUsersParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listAccountUsers>>, TError, TData>
    >;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListAccountUsersQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listAccountUsers>>> = ({ signal }) =>
    listAccountUsers(params, signal);

  const customOptions = addMetaToQuery(
    { ...queryOptions, queryKey, queryFn },
    { params },
    {
      url: `/v1/users`,
      operationId: 'app.controllers.account_users_controller.list_account_users',
      operationName: 'listAccountUsers',
    }
  );

  return customOptions as UseSuspenseQueryOptions<
    Awaited<ReturnType<typeof listAccountUsers>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListAccountUsersSuspenseQueryResult = NonNullable<
  Awaited<ReturnType<typeof listAccountUsers>>
>;
export type ListAccountUsersSuspenseQueryError = Error;

export function useListAccountUsersSuspense<
  TData = Awaited<ReturnType<typeof listAccountUsers>>,
  TError = Error,
>(
  params: undefined | ListAccountUsersParams,
  options: {
    query: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listAccountUsers>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListAccountUsersSuspense<
  TData = Awaited<ReturnType<typeof listAccountUsers>>,
  TError = Error,
>(
  params?: ListAccountUsersParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listAccountUsers>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListAccountUsersSuspense<
  TData = Awaited<ReturnType<typeof listAccountUsers>>,
  TError = Error,
>(
  params?: ListAccountUsersParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listAccountUsers>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Account Users
 */

export function useListAccountUsersSuspense<
  TData = Awaited<ReturnType<typeof listAccountUsers>>,
  TError = Error,
>(
  params?: ListAccountUsersParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listAccountUsers>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListAccountUsersSuspenseQueryOptions(params, options);

  const query = useSuspenseQuery(queryOptions, queryClient) as UseSuspenseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Accepts an account invitation using the token from the invite email. No login required.
 * @summary Accept an Invitation to an Account
 */
export const acceptAccountUserInvite = (
  params: AcceptAccountUserInviteParams,
  signal?: AbortSignal
) => {
  return customFetch<AcceptInviteResponse>({
    url: `/v1/users/accept`,
    method: 'GET',
    params,
    signal,
  });
};

export const getAcceptAccountUserInviteQueryKey = (params?: AcceptAccountUserInviteParams) => {
  return [`/v1/users/accept`, ...(params ? [params] : [])] as const;
};

export const getAcceptAccountUserInviteQueryOptions = <
  TData = Awaited<ReturnType<typeof acceptAccountUserInvite>>,
  TError = Error,
>(
  params: AcceptAccountUserInviteParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof acceptAccountUserInvite>>, TError, TData>
    >;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getAcceptAccountUserInviteQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof acceptAccountUserInvite>>> = ({
    signal,
  }) => acceptAccountUserInvite(params, signal);

  const customOptions = addMetaToQuery(
    { ...queryOptions, queryKey, queryFn },
    { params },
    {
      url: `/v1/users/accept`,
      operationId: 'app.controllers.account_users_controller.accept_account_user_invite',
      operationName: 'acceptAccountUserInvite',
    }
  );

  return customOptions as UseQueryOptions<
    Awaited<ReturnType<typeof acceptAccountUserInvite>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type AcceptAccountUserInviteQueryResult = NonNullable<
  Awaited<ReturnType<typeof acceptAccountUserInvite>>
>;
export type AcceptAccountUserInviteQueryError = Error;

export function useAcceptAccountUserInvite<
  TData = Awaited<ReturnType<typeof acceptAccountUserInvite>>,
  TError = Error,
>(
  params: AcceptAccountUserInviteParams,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof acceptAccountUserInvite>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof acceptAccountUserInvite>>,
          TError,
          Awaited<ReturnType<typeof acceptAccountUserInvite>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useAcceptAccountUserInvite<
  TData = Awaited<ReturnType<typeof acceptAccountUserInvite>>,
  TError = Error,
>(
  params: AcceptAccountUserInviteParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof acceptAccountUserInvite>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof acceptAccountUserInvite>>,
          TError,
          Awaited<ReturnType<typeof acceptAccountUserInvite>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useAcceptAccountUserInvite<
  TData = Awaited<ReturnType<typeof acceptAccountUserInvite>>,
  TError = Error,
>(
  params: AcceptAccountUserInviteParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof acceptAccountUserInvite>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Accept an Invitation to an Account
 */

export function useAcceptAccountUserInvite<
  TData = Awaited<ReturnType<typeof acceptAccountUserInvite>>,
  TError = Error,
>(
  params: AcceptAccountUserInviteParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof acceptAccountUserInvite>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getAcceptAccountUserInviteQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

export const getAcceptAccountUserInviteSuspenseQueryOptions = <
  TData = Awaited<ReturnType<typeof acceptAccountUserInvite>>,
  TError = Error,
>(
  params: AcceptAccountUserInviteParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof acceptAccountUserInvite>>, TError, TData>
    >;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getAcceptAccountUserInviteQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof acceptAccountUserInvite>>> = ({
    signal,
  }) => acceptAccountUserInvite(params, signal);

  const customOptions = addMetaToQuery(
    { ...queryOptions, queryKey, queryFn },
    { params },
    {
      url: `/v1/users/accept`,
      operationId: 'app.controllers.account_users_controller.accept_account_user_invite',
      operationName: 'acceptAccountUserInvite',
    }
  );

  return customOptions as UseSuspenseQueryOptions<
    Awaited<ReturnType<typeof acceptAccountUserInvite>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type AcceptAccountUserInviteSuspenseQueryResult = NonNullable<
  Awaited<ReturnType<typeof acceptAccountUserInvite>>
>;
export type AcceptAccountUserInviteSuspenseQueryError = Error;

export function useAcceptAccountUserInviteSuspense<
  TData = Awaited<ReturnType<typeof acceptAccountUserInvite>>,
  TError = Error,
>(
  params: AcceptAccountUserInviteParams,
  options: {
    query: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof acceptAccountUserInvite>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useAcceptAccountUserInviteSuspense<
  TData = Awaited<ReturnType<typeof acceptAccountUserInvite>>,
  TError = Error,
>(
  params: AcceptAccountUserInviteParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof acceptAccountUserInvite>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useAcceptAccountUserInviteSuspense<
  TData = Awaited<ReturnType<typeof acceptAccountUserInvite>>,
  TError = Error,
>(
  params: AcceptAccountUserInviteParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof acceptAccountUserInvite>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Accept an Invitation to an Account
 */

export function useAcceptAccountUserInviteSuspense<
  TData = Awaited<ReturnType<typeof acceptAccountUserInvite>>,
  TError = Error,
>(
  params: AcceptAccountUserInviteParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof acceptAccountUserInvite>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getAcceptAccountUserInviteSuspenseQueryOptions(params, options);

  const query = useSuspenseQuery(queryOptions, queryClient) as UseSuspenseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Permanently removes a user's access to your Rocket.net account.
 * @summary Remove Account User
 */
export const deleteAccountUser = (userId: string) => {
  return customFetch<SuccessResponse>({ url: `/v1/users/${userId}`, method: 'DELETE' });
};

export const getDeleteAccountUserMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteAccountUser>>,
    TError,
    { userId: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteAccountUser>>,
  TError,
  { userId: string },
  TContext
> => {
  const mutationKey = ['deleteAccountUser'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteAccountUser>>,
    { userId: string }
  > = props => {
    const { userId } = props ?? {};

    return deleteAccountUser(userId);
  };

  const customOptions = addMetaToMutation(
    { ...mutationOptions, mutationFn },
    { url: `/v1/users/{userId}` },
    {
      operationId: 'app.controllers.account_users_controller.delete_account_user',
      operationName: 'deleteAccountUser',
    }
  );

  return customOptions;
};

export type DeleteAccountUserMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteAccountUser>>
>;

export type DeleteAccountUserMutationError = Error;

/**
 * @summary Remove Account User
 */
export const useDeleteAccountUser = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof deleteAccountUser>>,
      TError,
      { userId: string },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof deleteAccountUser>>,
  TError,
  { userId: string },
  TContext
> => {
  const mutationOptions = getDeleteAccountUserMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Returns one user's profile, roles, two-factor status, and invite status.
 * @summary Get Account User details
 */
export const findAccountUser = (userId: string, signal?: AbortSignal) => {
  return customFetch<UserResponse>({ url: `/v1/users/${userId}`, method: 'GET', signal });
};

export const getFindAccountUserQueryKey = (userId?: string) => {
  return [`/v1/users/${userId}`] as const;
};

export const getFindAccountUserQueryOptions = <
  TData = Awaited<ReturnType<typeof findAccountUser>>,
  TError = Error,
>(
  userId: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof findAccountUser>>, TError, TData>>;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getFindAccountUserQueryKey(userId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof findAccountUser>>> = ({ signal }) =>
    findAccountUser(userId, signal);

  const customOptions = addMetaToQuery(
    { ...queryOptions, queryKey, queryFn },
    { userId },
    {
      url: `/v1/users/${userId}`,
      operationId: 'app.controllers.account_users_controller.find_account_user',
      operationName: 'findAccountUser',
    }
  );

  return customOptions as UseQueryOptions<
    Awaited<ReturnType<typeof findAccountUser>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type FindAccountUserQueryResult = NonNullable<Awaited<ReturnType<typeof findAccountUser>>>;
export type FindAccountUserQueryError = Error;

export function useFindAccountUser<
  TData = Awaited<ReturnType<typeof findAccountUser>>,
  TError = Error,
>(
  userId: string,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof findAccountUser>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof findAccountUser>>,
          TError,
          Awaited<ReturnType<typeof findAccountUser>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useFindAccountUser<
  TData = Awaited<ReturnType<typeof findAccountUser>>,
  TError = Error,
>(
  userId: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof findAccountUser>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof findAccountUser>>,
          TError,
          Awaited<ReturnType<typeof findAccountUser>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useFindAccountUser<
  TData = Awaited<ReturnType<typeof findAccountUser>>,
  TError = Error,
>(
  userId: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof findAccountUser>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Account User details
 */

export function useFindAccountUser<
  TData = Awaited<ReturnType<typeof findAccountUser>>,
  TError = Error,
>(
  userId: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof findAccountUser>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getFindAccountUserQueryOptions(userId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

export const getFindAccountUserSuspenseQueryOptions = <
  TData = Awaited<ReturnType<typeof findAccountUser>>,
  TError = Error,
>(
  userId: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof findAccountUser>>, TError, TData>
    >;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getFindAccountUserQueryKey(userId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof findAccountUser>>> = ({ signal }) =>
    findAccountUser(userId, signal);

  const customOptions = addMetaToQuery(
    { ...queryOptions, queryKey, queryFn },
    { userId },
    {
      url: `/v1/users/${userId}`,
      operationId: 'app.controllers.account_users_controller.find_account_user',
      operationName: 'findAccountUser',
    }
  );

  return customOptions as UseSuspenseQueryOptions<
    Awaited<ReturnType<typeof findAccountUser>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type FindAccountUserSuspenseQueryResult = NonNullable<
  Awaited<ReturnType<typeof findAccountUser>>
>;
export type FindAccountUserSuspenseQueryError = Error;

export function useFindAccountUserSuspense<
  TData = Awaited<ReturnType<typeof findAccountUser>>,
  TError = Error,
>(
  userId: string,
  options: {
    query: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof findAccountUser>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useFindAccountUserSuspense<
  TData = Awaited<ReturnType<typeof findAccountUser>>,
  TError = Error,
>(
  userId: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof findAccountUser>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useFindAccountUserSuspense<
  TData = Awaited<ReturnType<typeof findAccountUser>>,
  TError = Error,
>(
  userId: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof findAccountUser>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Account User details
 */

export function useFindAccountUserSuspense<
  TData = Awaited<ReturnType<typeof findAccountUser>>,
  TError = Error,
>(
  userId: string,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof findAccountUser>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getFindAccountUserSuspenseQueryOptions(userId, options);

  const query = useSuspenseQuery(queryOptions, queryClient) as UseSuspenseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Changes a user's roles or whether two-factor authentication is required.
 * @summary Update Account User roles
 */
export const modifyAccountUser = (userId: string, updateUserRequest: UpdateUserRequest) => {
  return customFetch<UserResponse>({
    url: `/v1/users/${userId}`,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    data: updateUserRequest,
  });
};

export const getModifyAccountUserMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof modifyAccountUser>>,
    TError,
    { userId: string; data: UpdateUserRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof modifyAccountUser>>,
  TError,
  { userId: string; data: UpdateUserRequest },
  TContext
> => {
  const mutationKey = ['modifyAccountUser'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof modifyAccountUser>>,
    { userId: string; data: UpdateUserRequest }
  > = props => {
    const { userId, data } = props ?? {};

    return modifyAccountUser(userId, data);
  };

  const customOptions = addMetaToMutation(
    { ...mutationOptions, mutationFn },
    { url: `/v1/users/{userId}` },
    {
      operationId: 'app.controllers.account_users_controller.modify_account_user',
      operationName: 'modifyAccountUser',
    }
  );

  return customOptions;
};

export type ModifyAccountUserMutationResult = NonNullable<
  Awaited<ReturnType<typeof modifyAccountUser>>
>;
export type ModifyAccountUserMutationBody = UpdateUserRequest;
export type ModifyAccountUserMutationError = Error;

/**
 * @summary Update Account User roles
 */
export const useModifyAccountUser = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof modifyAccountUser>>,
      TError,
      { userId: string; data: UpdateUserRequest },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof modifyAccountUser>>,
  TError,
  { userId: string; data: UpdateUserRequest },
  TContext
> => {
  const mutationOptions = getModifyAccountUserMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Sets the initial password after accepting a team invite. The invite link expires after one use or 30 minutes; request a re-invite if it expires.
 * @summary Set password as Account User
 */
export const setAccountUserPassword = (
  userId: string,
  setUserPasswordRequest: SetUserPasswordRequest,
  signal?: AbortSignal
) => {
  return customFetch<SuccessResponse>({
    url: `/v1/users/${userId}/password`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: setUserPasswordRequest,
    signal,
  });
};

export const getSetAccountUserPasswordMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof setAccountUserPassword>>,
    TError,
    { userId: string; data: SetUserPasswordRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof setAccountUserPassword>>,
  TError,
  { userId: string; data: SetUserPasswordRequest },
  TContext
> => {
  const mutationKey = ['setAccountUserPassword'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof setAccountUserPassword>>,
    { userId: string; data: SetUserPasswordRequest }
  > = props => {
    const { userId, data } = props ?? {};

    return setAccountUserPassword(userId, data);
  };

  const customOptions = addMetaToMutation(
    { ...mutationOptions, mutationFn },
    { url: `/v1/users/{userId}/password` },
    {
      operationId: 'app.controllers.account_users_controller.set_account_user_password',
      operationName: 'setAccountUserPassword',
    }
  );

  return customOptions;
};

export type SetAccountUserPasswordMutationResult = NonNullable<
  Awaited<ReturnType<typeof setAccountUserPassword>>
>;
export type SetAccountUserPasswordMutationBody = SetUserPasswordRequest;
export type SetAccountUserPasswordMutationError = Error;

/**
 * @summary Set password as Account User
 */
export const useSetAccountUserPassword = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof setAccountUserPassword>>,
      TError,
      { userId: string; data: SetUserPasswordRequest },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof setAccountUserPassword>>,
  TError,
  { userId: string; data: SetUserPasswordRequest },
  TContext
> => {
  const mutationOptions = getSetAccountUserPasswordMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Sends a new invitation email. Previous invite links are invalidated.
 * @summary Resend Account User invite
 */
export const reinviteAccountUser = (userId: string, signal?: AbortSignal) => {
  return customFetch<SuccessResponse>({
    url: `/v1/users/${userId}/reinvite`,
    method: 'POST',
    signal,
  });
};

export const getReinviteAccountUserMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof reinviteAccountUser>>,
    TError,
    { userId: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof reinviteAccountUser>>,
  TError,
  { userId: string },
  TContext
> => {
  const mutationKey = ['reinviteAccountUser'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof reinviteAccountUser>>,
    { userId: string }
  > = props => {
    const { userId } = props ?? {};

    return reinviteAccountUser(userId);
  };

  const customOptions = addMetaToMutation(
    { ...mutationOptions, mutationFn },
    { url: `/v1/users/{userId}/reinvite` },
    {
      operationId: 'app.controllers.account_users_controller.reinvite_account_user',
      operationName: 'reinviteAccountUser',
    }
  );

  return customOptions;
};

export type ReinviteAccountUserMutationResult = NonNullable<
  Awaited<ReturnType<typeof reinviteAccountUser>>
>;

export type ReinviteAccountUserMutationError = Error;

/**
 * @summary Resend Account User invite
 */
export const useReinviteAccountUser = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof reinviteAccountUser>>,
      TError,
      { userId: string },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof reinviteAccountUser>>,
  TError,
  { userId: string },
  TContext
> => {
  const mutationOptions = getReinviteAccountUserMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
